import { useEffect, useCallback } from 'react';

/**
 * Return type for the useBodyScrollLock hook
 */
interface UseBodyScrollLockReturn {
  lockScroll: () => () => void;
  unlockScroll: () => void;
}

/**
 * A comprehensive hook for locking body scroll without causing layout shifts
 * This hook preserves scroll position and prevents content jump when locking/unlocking
 */
export function useBodyScrollLock(isLocked: boolean): UseBodyScrollLockReturn {
  // Function to lock scroll
  const lockScroll = useCallback((): (() => void) => {
    // Save current scroll position
    const scrollY = window.scrollY;
    
    // Lock scrolling with fixed position
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    
    // Return function to reset these values
    return () => {
      // Only restore if we've previously set position to fixed
      if (document.body.style.position === 'fixed') {
        // Get the scroll position from the body's top property
        const scrollY = parseInt(document.body.style.top || '0', 10) * -1;
        
        // Restore normal body styling
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY || 0);
      }
    };
  }, []);

  // Apply/remove scroll lock based on isLocked state
  useEffect(() => {
    if (!isLocked) return undefined;
    
    // Apply scroll lock
    const unlockScroll = lockScroll();
    
    // Cleanup function
    return () => {
      unlockScroll();
    };
  }, [isLocked, lockScroll]);
  
  // Return lock/unlock methods for manual control if needed
  return {
    lockScroll,
    unlockScroll: (): void => {
      // Only restore if we've previously set position to fixed
      if (document.body.style.position === 'fixed') {
        // Get the scroll position from the body's top property
        const scrollY = parseInt(document.body.style.top || '0', 10) * -1;
        
        // Restore normal body styling
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY || 0);
      }
    }
  };
}

